import React, { useRef, useContext } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import throttle from 'lodash.throttle';
import splotchCopy from '../../config/splotchCopy';
import TransitionText from '../common/TransitionText';
import Lottie from '../common/Lottie';
import Loader from '../../assets/lotties/Loader.json';
import variables from '../../theme/variables';
import { State } from '../../contexts/GlobalState';
import { usePreload } from '../primaries/Preloader';

const useStyles = createUseStyles(
    {
        wrap: {
            height: 'var(--app-height)',
            width: '100%',
        },

        inner: {
            position: 'fixed',
            height: 'var(--app-height)',
            width: '100%',
            top: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            paddingRight: 20,
            paddingLeft: 20,

            '@media (min-width: 1024px)': {
                paddingRight: 0,
                paddingLeft: 60,
            },
        },

        canvas: {
            '@media (max-width: 1023px)': {
                width: '100%',
                aspectRatio: '1080 / 1920',
                position: 'fixed',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)',
            },

            '@media (min-width: 1024px)': {
                height: 'var(--app-height)',
                aspectRatio: '1920 / 1080',
                position: 'fixed',
                right: 0,
                top: 0,
            },
        },

        copyContainer: {
            position: 'relative',
            width: '100%',
            height: '100%',

            '@media (min-width: 1024px)': {
                maxWidth: 360,
            },
        },

        copyItem: {
            position: 'absolute',
            bottom: 15,

            '@media (min-width: 1024px)': {
                top: '50%',
                left: 0,
                bottom: 'auto',
                transform: 'translateY(-50%)',
            },
        },

        title: {
            fontSize: 24,
            lineHeight: '30px',
            marginBottom: 20,

            '@media (min-width: 1024px)': {
                fontSize: 40,
                lineHeight: '50px',
            },
        },

        body: {
            fontSize: 16,
            lineHeight: '22px',

            '@media (min-width: 1024px)': {
                fontSize: 18,
                lineHeight: '28px',
            },

            '& p': {
                marginBottom: '1em',
            },
        },

        loading: {
            position: 'fixed',
            width: '100vw',
            height: 'var(--app-height)',
            left: 0,
            top: 0,
            background: variables.palette.grey.dark,
            zIndex: 1,
        },
    },
    { name: 'Splotch' }
);

const scrollHeight = 6000;

export default function Splotch() {
    const classes = useStyles();
    const { preloaded, noOfFrames } = usePreload();
    const { setReturnButtonTheme, screenOrientation } = useContext(State);

    const canvasRef = useRef(null);
    const contextRef = useRef(null);
    const imageRef = useRef(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [currentFrame, setCurrentFrame] = useState(1);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (preloaded) {
            setLoading(false);
            setReturnButtonTheme('dark');
        } else {
            setReturnButtonTheme('light');
        }
    }, [preloaded]);

    useEffect(() => {
        if (!canvasRef.current) return;

        if (screenOrientation === 'desktop') {
            canvasRef.current.width = 1920;
            canvasRef.current.height = 1080;
        } else {
            canvasRef.current.width = 1080;
            canvasRef.current.height = 1920;
        }

        contextRef.current = canvasRef.current.getContext('2d');
    }, [screenOrientation]);

    useEffect(() => {
        window.addEventListener(
            'scroll',
            throttle(function () {
                let scrollHeight = Math.max(
                    document.body.scrollHeight,
                    document.documentElement.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.offsetHeight,
                    document.body.clientHeight,
                    document.documentElement.clientHeight
                );
                const scrollPercentage =
                    window.scrollY / (scrollHeight - window.innerHeight);

                setScrollProgress(scrollPercentage);
            }, 100)
        );
    }, []);

    const getIndexString = (index) => {
        let index_string = '';
        if (index < 10) {
            index_string = `00${index}`;
        } else if (index < 100) {
            index_string = `0${index}`;
        } else {
            index_string = `${index}`;
        }

        return index_string;
    };

    const updateImage = (index, context = {}) => {
        if (!contextRef.current) return;

        let index_string = getIndexString(index);

        imageRef.current = `${window.rootUrl}/img/splotch/${screenOrientation}/video_${index_string}.webp`; //currentFrame(sequence, index);

        const img = new Image();
        img.src = imageRef.current;

        img.onload = function () {
            if (img.src.endsWith(imageRef.current)) {
                contextRef?.current?.drawImage(img, 0, 0);
            } else {
            }
        };
    };

    useEffect(() => {
        updateImage(currentFrame, { event: 'mount' });

        return () => {
            updateImage(currentFrame, { event: 'unmount' });
        };
    }, [canvasRef, contextRef, screenOrientation]);

    useEffect(() => {
        const frameIndex = scrollProgress * noOfFrames;
        const index_rounded = Math.max(Math.floor(frameIndex), 1);
        setCurrentFrame(index_rounded);

        requestAnimationFrame(() =>
            updateImage(index_rounded, { event: 'on scroll change' })
        );
    }, [scrollProgress]);

    return (
        <div className={classes.wrap} style={{ height: scrollHeight }}>
            {loading && (
                <div className={classes.loading}>
                    <Lottie animationData={Loader} width="100%" height="100%" />
                </div>
            )}
            <canvas className={classes.canvas} ref={canvasRef}></canvas>
            <div className={classes.inner}>
                <div className={classes.copyContainer}>
                    {splotchCopy.map((item, index) => {
                        let itemState;

                        if (item.out < currentFrame) {
                            itemState = 'out';
                        } else if (item.in < currentFrame) {
                            itemState = 'in';
                        } else {
                            itemState = 'init';
                        }
                        return (
                            <div
                                key={index}
                                className={classes.copyItem}
                                style={{ zIndex: itemState === 'in' ? 1 : 0 }}
                            >
                                <TransitionText
                                    state={itemState}
                                    distance={{ init: 50, out: -100 }}
                                >
                                    <h4 className={classes.title}>
                                        {item.title}
                                    </h4>
                                </TransitionText>
                                <TransitionText
                                    state={itemState}
                                    distance={{ init: 100, out: -50 }}
                                >
                                    <div className={classes.body}>
                                        {item.body}
                                    </div>
                                </TransitionText>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
